const MAX_DIMENSION = 2048;
const JPEG_QUALITY = 0.8;

/**
 * Resizes an image File if it exceeds maxDimension on either axis.
 * Uses the Canvas API. Returns the original file if not an image
 * or already within bounds. Output is JPEG for consistent compression.
 */
export async function resizeImageFile(file: File, maxDimension: number = MAX_DIMENSION, quality: number = JPEG_QUALITY): Promise<File> {
  if (!file.type.startsWith('image/')) {
    return file;
  }

  try {
    const img = await loadImage(file);

    if (img.width <= maxDimension && img.height <= maxDimension) {
      return file;
    }

    let newWidth: number;
    let newHeight: number;
    if (img.width >= img.height) {
      newWidth = maxDimension;
      newHeight = Math.round((img.height / img.width) * maxDimension);
    } else {
      newHeight = maxDimension;
      newWidth = Math.round((img.width / img.height) * maxDimension);
    }

    const canvas = document.createElement('canvas');
    canvas.width = newWidth;
    canvas.height = newHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return file;
    }

    ctx.drawImage(img, 0, 0, newWidth, newHeight);

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));

    if (!blob) {
      return file;
    }

    const name = file.name.replace(/\.[^.]+$/, '.jpg');
    return new File([blob], name, { type: 'image/jpeg' });
  } catch {
    return file;
  }
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };
    img.src = url;
  });
}
